import { useMemo } from "react";
import { Namespace, useT } from "@racoon/i18n";
import type { FilterFilterParam } from "@racoon/ui";
import type { LocationCriteria } from "../lib/location-criteria";

type OptionItem = { id: string; name: string };

/**
 * The filter sheet's fields, their values read off `criteria`. Select options come
 * from dedicated list endpoints, not from the loaded rows — a server page only
 * carries a slice of the possible values.
 */
export const useLocationFilterParams = (
  criteria: LocationCriteria,
  optionSources: {
    /** The active types in the settings' order; none hides the filter. */
    pickupLocationTypes: OptionItem[];
    customers: OptionItem[];
    containerTypes: OptionItem[];
    routeGroups: OptionItem[];
    trucks: OptionItem[];
  },
): FilterFilterParam[] => {
  const t = useT();
  const {
    typeId,
    customerId,
    containerTypeId,
    routeGroupId,
    truckId,
    withoutRouteGroup,
    appAccess,
  } = criteria;

  return useMemo(() => {
    const byName = (a: OptionItem, b: OptionItem) => a.name.localeCompare(b.name);
    // A ticked box reads as "yes"; the option is what names it on the active
    // filter chip.
    const ticked = [{ id: "yes", name: t(Namespace.Table, "yes") }];
    // With no types every location is «Не указано»: nothing to narrow, so no filter,
    // unless the URL already filters by type: it must stay visible to be cleared.
    const typeFilter =
      optionSources.pickupLocationTypes.length > 0 || typeId !== ""
        ? [
            {
              key: "pickup_location_type_id",
              label: t(Namespace.PickupLocation, "type"),
              type: "select" as const,
              options: [
                { id: "0", name: t(Namespace.Settings, "organizationLabels.undefined") },
                ...optionSources.pickupLocationTypes,
              ],
              value: typeId,
            },
          ]
        : [];
    return [
      ...typeFilter,
      {
        key: "customer_id",
        label: t(Namespace.Table, "organizaation"),
        type: "select" as const,
        searchable: true,
        options: [...optionSources.customers].sort(byName),
        value: customerId,
      },
      {
        key: "container_type_id",
        label: t(Namespace.Table, "containerGroups"),
        type: "select" as const,
        searchable: true,
        options: [...optionSources.containerTypes].sort(byName),
        value: containerTypeId,
      },
      {
        key: "route_group_id",
        // Not the table's own key: column headers are upper-cased, filter
        // labels are not.
        label: t(Namespace.Routes, "routeGroupFilter"),
        type: "select" as const,
        searchable: true,
        options: [...optionSources.routeGroups].sort(byName),
        value: routeGroupId,
      },
      {
        key: "truck_id",
        label: t(Namespace.PickupLocation, "groups.responsibleTruck"),
        type: "select" as const,
        searchable: true,
        // Plates are read without their spaces, the way they are typed.
        searchMatch: "compact" as const,
        options: [...optionSources.trucks].sort(byName),
        value: truckId,
      },
      {
        key: "without_route_group",
        label: t(Namespace.PickupLocation, "cascade.withoutRouteGroup"),
        type: "checkbox" as const,
        options: ticked,
        value: withoutRouteGroup,
      },
      {
        key: "app_access",
        label: t(Namespace.Table, "app"),
        type: "select" as const,
        options: [
          { id: "yes", name: t(Namespace.Table, "yes") },
          { id: "no", name: t(Namespace.Table, "no") },
        ],
        value: appAccess,
      },
    ];
  }, [
    t,
    optionSources,
    typeId,
    customerId,
    containerTypeId,
    routeGroupId,
    truckId,
    withoutRouteGroup,
    appAccess,
  ]);
};
